import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Calendar, Clock, ArrowRight, Gift, Hotel } from "lucide-react";
import { useContent } from "../context/ContentContext";
import { Countdown } from "../components/Countdown";
import { AddToCalendar } from "../components/AddToCalendar";
import { Reveal } from "../components/Reveal";

export default function Home() {
  const { content } = useContent();
  const hero = content?.home?.hero_image;
  const welcome = content?.home?.welcome;

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative">
        <div className="grid lg:grid-cols-2 min-h-[88vh]">
          <div className="flex items-center order-2 lg:order-1 px-6 sm:px-12 lg:px-16 py-16">
            <Reveal>
              <p className="overline text-olive mb-6">We're getting married</p>
              <h1 className="font-serif text-6xl sm:text-7xl xl:text-8xl leading-[0.9] text-matte">
                Vernetta<br /><span className="text-gold italic">&amp;</span> Quin
              </h1>
              <div className="mt-8 space-y-2 text-taupe">
                <p className="flex items-center gap-3 text-lg"><Calendar size={18} className="text-olive" /> February 20, 2027</p>
                <p className="flex items-center gap-3 text-lg"><MapPin size={18} className="text-olive" /> Alpharetta, Georgia</p>
              </div>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/rsvp" className="btn-olive rounded-full px-9 py-4 overline" data-testid="hero-rsvp-btn">RSVP</Link>
                <Link to="/wedding-details" className="btn-outline rounded-full px-9 py-4 overline" data-testid="hero-details-btn">View Wedding Details</Link>
              </div>
            </Reveal>
          </div>
          <div className="order-1 lg:order-2 relative min-h-[45vh] lg:min-h-full overflow-hidden">
            {hero && <img src={hero} alt="Vernetta and Quin" className="absolute inset-0 w-full h-full object-cover animate-reveal" />}
            <div className="absolute inset-0 ring-1 ring-inset ring-gold/20" />
          </div>
        </div>
      </section>

      {/* COUNTDOWN */}
      <section className="bg-matte py-16 sm:py-20">
        <div className="max-w-5xl mx-auto px-5 text-center">
          <p className="overline text-gold mb-8">Counting down to forever</p>
          <div className="flex justify-center"><Countdown light /></div>
          <div className="mt-10 flex justify-center"><AddToCalendar /></div>
        </div>
      </section>

      {/* WELCOME */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 py-20 sm:py-28 text-center">
        <Reveal>
          <p className="overline text-olive mb-6">Welcome</p>
          <p className="font-serif text-2xl sm:text-3xl lg:text-4xl leading-relaxed text-matte">{welcome}</p>
          <div className="gold-line mx-auto mt-10" />
        </Reveal>
      </section>

      {/* DETAILS PREVIEW */}
      <section className="stone-panel border-y border-taupe/30 py-20">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-8">
            {[
              { icon: Calendar, label: "The Date", value: "Saturday", sub: "February 20, 2027" },
              { icon: MapPin, label: "The Venue", value: "The Metropolitan Club", sub: "Indoor Ceremony & Reception" },
              { icon: MapPin, label: "The City", value: "Alpharetta", sub: "Georgia" },
              { icon: Clock, label: "RSVP By", value: "October 15", sub: "2026" },
            ].map((it, i) => (
              <Reveal key={it.label} delay={i * 90}>
                <div className="bg-soft-white/70 border border-taupe/40 rounded-lg p-8 h-full text-center">
                  <it.icon className="mx-auto text-olive" size={26} />
                  <p className="overline text-taupe mt-4">{it.label}</p>
                  <p className="font-serif text-2xl mt-2 text-matte">{it.value}</p>
                  <p className="text-sm text-taupe mt-1">{it.sub}</p>
                </div>
              </Reveal>
            ))}
            <Reveal delay={360}>
              <div className="bg-olive text-soft-white border border-olive rounded-lg p-8 h-full text-center flex flex-col" data-testid="home-hotel-tile">
                <Hotel className="mx-auto text-gold" size={26} />
                <p className="overline text-soft-white/70 mt-4">Host Hotel</p>
                <p className="font-serif text-2xl mt-2">Courtyard by Marriott</p>
                <p className="text-sm text-soft-white/70 mt-1">Alpharetta Avalon Area</p>
                <a href="https://www.marriott.com/event-reservations/reservation-link.mi?id=1788582015830&key=GRP&app=resvlink"
                  target="_blank" rel="noopener noreferrer"
                  className="mt-auto pt-5 inline-flex items-center justify-center gap-2 overline text-gold hover:gap-3 transition-all" data-testid="home-hotel-book-btn">
                  Book Now <ArrowRight size={15} />
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CALLOUTS */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-20 grid md:grid-cols-2 gap-8">
        <Reveal>
          <div className="bg-olive text-soft-white rounded-lg p-10 sm:p-12 h-full flex flex-col justify-between">
            <div>
              <Gift className="text-gold" size={28} />
              <h2 className="font-serif text-4xl mt-5">The Registry</h2>
              <p className="mt-4 text-soft-white/80">Your presence at our wedding is the greatest gift. For those who have asked, we are kindly requesting contributions to our HoneyFund in lieu of physical gifts.</p>
            </div>
            <Link to="/registry" className="inline-flex items-center gap-2 mt-8 overline text-gold hover:gap-3 transition-all" data-testid="home-registry-link">
              View Registry <ArrowRight size={16} />
            </Link>
          </div>
        </Reveal>
        <Reveal delay={120}>
          <div className="bg-soft-white border border-taupe/40 rounded-lg p-10 sm:p-12 h-full flex flex-col justify-between">
            <div>
              <Calendar className="text-olive" size={28} />
              <h2 className="font-serif text-4xl mt-5 text-matte">Kindly Respond</h2>
              <p className="mt-4 text-taupe">We can't wait to celebrate with you. This is an invitation-only celebration — please let us know if you'll be joining us by October 15, 2026.</p>
              <p className="mt-4 text-matte font-medium" data-testid="home-limited-guest-note">We have a very limited guest list and can only accommodate the people listed on the invite.</p>
            </div>
            <Link to="/rsvp" className="inline-flex items-center gap-2 mt-8 overline text-olive hover:gap-3 transition-all" data-testid="home-rsvp-link">
              RSVP Now <ArrowRight size={16} />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
